import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  Product,
  Category,
  School,
  Reservation,
  NewsItem,
  SiteSettings,
  ImpactStats,
  BasketItem,
  ReservationStatus,
  CustomerMessage,
  MessageStatus,
  OutboundEmail,
  UserProfile,
  UserRole
} from '../types';
import {
  INITIAL_SETTINGS,
  INITIAL_STATS,
  INITIAL_CATEGORIES,
  INITIAL_SCHOOLS,
  INITIAL_PRODUCTS,
  INITIAL_NEWS,
  formatOpeningHours,
  DEFAULT_OPENING_SCHEDULE
} from '../data/imminghamData';
import { seedFirestoreIfEmpty } from '../services/seedService';
import { getFriendlyAuthErrorMessage } from '../utils/authErrorUtils';
import { subscribeProducts, addProduct as addProdFS, updateProduct as updateProdFS, deleteProduct as deleteProdFS } from '../services/productsService';
import { getTotalStock, getSizeStock, adjustSizeStock as adjustSizeStockFS } from '../services/inventoryService';
import {
  subscribeReservations,
  createReservation as createResFS,
  updateReservationStatus as updateResStatusFS,
  deleteReservation as deleteResFS,
  updateReservationPhone as updatePhoneFS,
  addReservationCustomerNote as addCustNoteFS,
  requestReservationDateChange as requestDateChangeFS,
  decideReservationDateChange as decideDateChangeFS,
  cancelReservationByCustomer as cancelResByCustFS,
  markReservationCollected as markCollectedFS,
  purgeExpiredArchivedReservations
} from '../services/reservationsService';
import {
  subscribeVolunteerNotifications,
  dismissVolunteerNotification as dismissVolunteerNotifFS,
  VolunteerNotification
} from '../services/notificationsService';
import {
  subscribeSchools,
  addSchool as addSchoolFS,
  updateSchool as updateSchoolFS,
  deleteSchool as deleteSchoolFS,
  reorderSchools as reorderSchoolsFS
} from '../services/schoolsService';
import {
  subscribeCategories,
  addCategory as addCategoryFS,
  updateCategory as updateCategoryFS,
  deleteCategory as deleteCategoryFS,
  swapCategoryOrder as swapCategoryOrderFS
} from '../services/categoriesService';
import { subscribeNews, addNewsItem as addNewsFS, updateNewsItem as updateNewsFS, deleteNewsItem as deleteNewsFS } from '../services/newsService';
import { subscribeSettings, updateSettings as updateSettingsFS, subscribeStats, updateStats as updateStatsFS } from '../services/settingsService';
import {
  subscribeCustomerMessages,
  createCustomerMessage as createMsgFS,
  updateCustomerMessageStatus as updateMsgStatusFS,
  addMessageReplyOrNote as addMsgReplyFS,
  assignCustomerMessage as assignMsgFS,
  deleteCustomerMessage as deleteMsgFS
} from '../services/messagesService';
import {
  subscribeAuthState,
  loginAdminWithEmail,
  registerAdminWithEmail as registerAdminWithEmailAuth,
  sendPasswordResetEmail as sendPasswordResetEmailAuth,
  logoutAdmin as logoutAdminAuth
} from '../services/authService';
import { perfDiagnostics } from '../services/perfDiagnostics';

import {
  EmailLog,
  EmailSettings,
  EmailTemplateType
} from '../types';
import {
  subscribeEmailSettings,
  updateEmailSettings as updateEmailSettingsFS,
  subscribeEmailLogs,
  sendTestEmail as sendTestEmailFS,
  cleanupOldEmailLogs
} from '../services/emailService';
import { DEFAULT_EMAIL_SETTINGS } from '../services/emailTemplates';
import { generateReferenceNumber, generateShortReservationCode, getCustomerReference } from '../utils/referenceGenerator';
import { hasPermission as checkUserPermission, isAdministrator as checkIsAdministrator, AppPermission } from '../utils/permissions';
import {
  ensureUserProfile,
  repairUserProfile as repairUserFS,
  subscribeUserProfile,
  subscribeAllUsers,
  createVolunteerAccount as createVolunteerFS,
  updateUserRoleOrStatus as updateRoleFS,
  deleteVolunteerDoc as deleteUserFS
} from '../services/userService';

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'info' | 'error';
}

interface ShopContextType {
  products: Product[];
  categories: Category[];
  schools: School[];
  allSchools: School[];
  reservations: Reservation[];
  news: NewsItem[];
  settings: SiteSettings;
  stats: ImpactStats;
  basket: BasketItem[];
  customerMessages: CustomerMessage[];
  isLoadingData: boolean;
  
  // Navigation & UI state
  activePage: string;
  setActivePage: (page: string) => void;
  selectedSchoolFilter: string;
  setSelectedSchoolFilter: (schoolId: string) => void;
  selectedCategoryFilter: string;
  setSelectedCategoryFilter: (catId: string) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  
  isBasketOpen: boolean;
  setIsBasketOpen: (open: boolean) => void;
  selectedProductDetail: Product | null;
  openProductDetail: (p: Product) => void;
  closeProductDetail: () => void;
  latestReservation: Reservation | null;
  setLatestReservation: (res: Reservation | null) => void;
  
  // Basket actions
  addToBasket: (product: Product, selectedSize: string, quantity?: number) => void;
  removeFromBasket: (productId: string, selectedSize: string) => void;
  updateBasketQuantity: (productId: string, selectedSize: string, quantity: number) => void;
  clearBasket: () => void;
  
  // Reservation actions
  createReservation: (details: {
    parentName: string;
    email: string;
    phone: string;
    preferredCollectionDay: string;
    optionalMessage?: string;
  }) => Promise<Reservation>;
  updateReservationStatus: (id: string, status: ReservationStatus, volunteerName?: string) => Promise<void>;
  deleteReservation: (id: string) => void;
  updateReservationPhone: (id: string, newPhone: string) => Promise<void>;
  addReservationCustomerNote: (id: string, noteMessage: string, parentName?: string) => Promise<void>;
  requestReservationDateChange: (id: string, requestedDate: string, notes?: string) => Promise<void>;
  decideReservationDateChange: (
    reservation: Reservation,
    decision: 'Approved' | 'Declined' | 'Rejected',
    decisionNotes?: string,
    volunteerName?: string
  ) => Promise<void>;
  markReservationCollected: (id: string, volunteerName?: string) => Promise<void>;
  cancelReservationByCustomer: (
    reservation: Reservation,
    cancellationReason?: string,
    emailSettings?: EmailSettings,
    siteSettings?: SiteSettings
  ) => Promise<void>;
  volunteerNotifications: VolunteerNotification[];
  dismissVolunteerNotification: (id: string) => Promise<void>;
  
  // Admin Stock actions
  addProduct: (product: Omit<Product, 'id' | 'createdAt'>) => Promise<string>;
  updateProduct: (id: string, updates: Partial<Product>) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  duplicateProduct: (id: string) => Promise<void>;
  adjustProductQuantity: (id: string, delta: number) => Promise<void>;
  adjustProductSizeStock: (productId: string, size: string, delta: number) => Promise<void>;
  
  // School Management
  addSchool: (school: Omit<School, 'id'>) => Promise<string>;
  updateSchool: (id: string, updates: Partial<School>) => Promise<void>;
  deleteSchool: (id: string) => Promise<void>;
  reorderSchools: (orderedSchools: School[]) => Promise<void>;

  // Category Management
  addCategory: (category: Category) => Promise<void>;
  updateCategory: (id: string, updates: Partial<Category>) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  swapCategoryOrder: (catA: { id: string; displayOrder: number }, catB: { id: string; displayOrder: number }) => Promise<void>;

  // News & Settings
  addNewsItem: (item: Omit<NewsItem, 'id' | 'createdAt'>) => Promise<void>;
  updateNewsItem: (id: string, updates: Partial<NewsItem>) => Promise<void>;
  deleteNewsItem: (id: string) => Promise<void>;
  updateSettings: (updates: Partial<SiteSettings>) => Promise<void>;
  updateStats: (updates: Partial<ImpactStats>) => Promise<void>;
  
  // Customer Messages
  createCustomerMessage: (msg: Omit<CustomerMessage, 'id' | 'createdAt' | 'status'>) => Promise<CustomerMessage>;
  updateCustomerMessageStatus: (id: string, status: MessageStatus) => Promise<void>;
  addCustomerMessageReply: (
    id: string,
    replyBody: string,
    isInternalNote?: boolean,
    customStatus?: MessageStatus
  ) => Promise<void>;
  assignCustomerMessage: (id: string, volunteerName: string) => Promise<void>;
  deleteCustomerMessage: (id: string) => Promise<void>;
  
  // Admin Auth & Role Management
  isAdminLoggedIn: boolean;
  isLoadingProfile: boolean;
  userProfileNotFound: { uid: string; email: string; displayName?: string } | null;
  userProfile: UserProfile | null;
  userRole: UserRole | null;
  isSuperAdmin: boolean;
  isAdministrator: boolean;
  isVolunteer: boolean;
  allUsers: UserProfile[];
  activeAdminTab: string;
  setActiveAdminTab: (tab: string) => void;
  loginAdmin: (email: string, password?: string) => Promise<boolean>;
  registerAdmin: (email: string, password: string, displayName?: string) => Promise<boolean>;
  sendPasswordReset: (email: string) => Promise<boolean>;
  logoutAdmin: () => void;
  repairMissingProfile: (role?: UserRole, permissions?: AppPermission[]) => Promise<void>;
  createVolunteerUser: (data: {
    email: string;
    password: string;
    displayName: string;
    phone?: string;
    role?: UserRole;
    rolePresetId?: string;
    roleTitle?: string;
    permissions?: AppPermission[];
    createdBy?: string;
  }) => Promise<void>;
  updateUserRoleOrStatus: (uid: string, updates: Partial<UserProfile>) => Promise<void>;
  deleteVolunteerUser: (uid: string) => Promise<void>;
  hasUserPermission: (permission: AppPermission) => boolean;
  
  // Email System
  emailSettings: EmailSettings;
  emailLogs: EmailLog[];
  updateEmailSettings: (updates: Partial<EmailSettings>) => Promise<void>;
  sendTestEmail: (recipientEmail: string) => Promise<{ success: boolean; deliveryStatus: string; error?: string }>;
  pruneEmailLogs: (retentionDays?: number) => Promise<{ deletedCount: number; cutoffDate: string }>;

  // Toasts
  toasts: Toast[];
  showToast: (message: string, type?: 'success' | 'info' | 'error') => void;
  removeToast: (id: string) => void;
  
  // PWA Prompt
  deferredInstallPrompt: any;
  installPWA: () => void;
}

const ShopContext = createContext<ShopContextType | undefined>(undefined);

const BASKET_STORAGE_KEY = 'ul_basket_v3';

const getPageFromPathname = (pathname: string): string => {
  const clean = pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
  if (clean.startsWith('reply')) {
    return 'reply';
  }
  switch (clean) {
    case 'privacy':
      return 'privacy';
    case 'terms':
      return 'terms';
    case 'shop':
      return 'shop';
    case 'donate':
      return 'donate';
    case 'how-it-works':
      return 'how-it-works';
    case 'about':
      return 'about';
    case 'faq':
      return 'faq';
    case 'contact':
      return 'contact';
    case 'news':
      return 'news';
    case 'admin':
      return 'admin';
    case 'confirmation':
      return 'confirmation';
    case 'manage-reservation':
      return 'manage-reservation';
    case 'reset-password':
      return 'reset-password';
    default:
      return 'home';
  }
};

export const ShopProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Live Firestore Data States
  const [products, setProducts] = useState<Product[]>(INITIAL_PRODUCTS);
  const [categories, setCategories] = useState<Category[]>(INITIAL_CATEGORIES);
  const [allSchools, setAllSchools] = useState<School[]>(INITIAL_SCHOOLS);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [news, setNews] = useState<NewsItem[]>(INITIAL_NEWS);
  const [settings, setSettings] = useState<SiteSettings>(INITIAL_SETTINGS);
  const [stats, setStats] = useState<ImpactStats>(INITIAL_STATS);
  const [customerMessages, setCustomerMessages] = useState<CustomerMessage[]>([]);
  const [volunteerNotifications, setVolunteerNotifications] = useState<VolunteerNotification[]>([]);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [emailSettings, setEmailSettings] = useState<EmailSettings>(DEFAULT_EMAIL_SETTINGS);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

  // Filter active schools for public dropdowns and inventory filters sorted alphabetically
  const schools = useMemo(() => {
    return [...allSchools]
      .filter((s) => s.active !== false)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allSchools]);

  // User Profile and Auth State
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState<boolean>(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState<boolean>(false);
  const [userProfileNotFound, setUserProfileNotFound] = useState<{ uid: string; email: string; displayName?: string } | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  // Local Basket State
  const [basket, setBasket] = useState<BasketItem[]>(() => {
    const saved = localStorage.getItem(BASKET_STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  // Navigation & Filter UI State
  const [activePage, setActivePageState] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return getPageFromPathname(window.location.pathname);
    }
    return 'home';
  });

  const setActivePage = (page: string) => {
    setActivePageState(page);
    try {
      const targetPath = page === 'home' ? '/' : `/${page}`;
      if (window.location.pathname !== targetPath) {
        window.history.pushState({ page }, '', targetPath);
      }
    } catch {
      // Non-fatal if browser restrictions apply
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      const page = getPageFromPathname(window.location.pathname);
      setActivePageState(page);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [selectedSchoolFilter, setSelectedSchoolFilter] = useState<string>('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  const [isBasketOpen, setIsBasketOpen] = useState<boolean>(false);
  const [selectedProductDetail, setSelectedProductDetail] = useState<Product | null>(null);
  const [latestReservation, setLatestReservation] = useState<Reservation | null>(null);

  // Admin Tab State
  const [activeAdminTab, setActiveAdminTab] = useState<string>('inventory');

  // Toasts
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState<any>(null);

  // 1. Public Firestore Subscriptions (Products, Schools, Categories, News, Settings, Stats)
  useEffect(() => {
    const publicUnsubs: (() => void)[] = [];
    perfDiagnostics.mark('PRODUCTS_LISTENER_STARTED');

    // Attach only public subscriptions on initial mount for all visitors
    publicUnsubs.push(
      subscribeProducts((data) => {
        perfDiagnostics.mark('PRODUCTS_DATA_RECEIVED');
        perfDiagnostics.recordCollectionRead('products', false);
        setProducts(data);
      })
    );
    publicUnsubs.push(
      subscribeSchools((data) => {
        perfDiagnostics.mark('SCHOOLS_DATA_RECEIVED');
        perfDiagnostics.recordCollectionRead('schools', false);
        const sorted = [...data].sort((a, b) => {
          if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
            return a.displayOrder - b.displayOrder;
          }
          return a.name.localeCompare(b.name);
        });
        setAllSchools(sorted);
      })
    );
    publicUnsubs.push(
      subscribeCategories((data) => {
        perfDiagnostics.mark('CATEGORIES_DATA_RECEIVED');
        perfDiagnostics.recordCollectionRead('categories', false);
        if (data && data.length > 0) {
          const sorted = [...data].sort((a, b) => {
            if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
              return a.displayOrder - b.displayOrder;
            }
            if (a.displayOrder !== undefined) return -1;
            if (b.displayOrder !== undefined) return 1;
            return a.name.localeCompare(b.name);
          });
          setCategories(sorted);
        } else {
          // Keep existing or initial categories fallback if snapshot is empty
          setCategories((prev) => (prev && prev.length > 0 ? prev : INITIAL_CATEGORIES));
        }
      })
    );
    publicUnsubs.push(
      subscribeNews((data) => {
        perfDiagnostics.mark('NEWS_DATA_RECEIVED');
        perfDiagnostics.recordCollectionRead('news', false);
        setNews(data);
      })
    );
    publicUnsubs.push(
      subscribeSettings((data) => {
        perfDiagnostics.mark('SETTINGS_DATA_RECEIVED');
        if (data) setSettings(data);
      })
    );
    publicUnsubs.push(
      subscribeStats((data) => {
        perfDiagnostics.mark('STATS_DATA_RECEIVED');
        if (data) setStats(data);
      })
    );

    setIsLoadingData(false);

    return () => {
      publicUnsubs.forEach((unsub) => unsub());
    };
  }, []);

  // 2. Auth State, User Profile & Authenticated Admin Subscriptions
  useEffect(() => {
    let adminUnsubs: (() => void)[] = [];

    const cleanupAdminSubscriptions = () => {
      adminUnsubs.forEach((unsub) => unsub());
      adminUnsubs = [];
    };

    const unsubAuth = subscribeAuthState(async (user) => {
      perfDiagnostics.mark('AUTH_RESOLVED');
      setIsAdminLoggedIn(!!user);

      // Clean up previous admin listeners on any auth state transition
      cleanupAdminSubscriptions();

      if (user) {
        setIsLoadingProfile(true);
        try {
          // Run startup seed check only for authenticated staff/volunteers
          seedFirestoreIfEmpty().catch((err) => {
            console.warn('Admin seed verification error:', err);
          });

          const profile = await ensureUserProfile(user);
          if (profile) {
            setUserProfile(profile);
            setUserProfileNotFound(null);

            adminUnsubs.push(
              subscribeUserProfile(user.uid, (p) => {
                if (p) {
                  setUserProfile(p);
                  setUserProfileNotFound(null);
                } else {
                  setUserProfileNotFound({ uid: user.uid, email: user.email || '', displayName: user.displayName || '' });
                }
              })
            );
          } else {
            console.error(`[AUTH ERROR] USER PROFILE NOT FOUND: Firebase Auth account exists (UID: ${user.uid}) but Firestore profile missing.`);
            setUserProfile(null);
            setUserProfileNotFound({
              uid: user.uid,
              email: user.email || '',
              displayName: user.displayName || ''
            });
          }

          // Permission-aware Live Listeners
          const canAccessReservations = checkUserPermission(profile, 'reservations');
          const canAccessMessages = checkUserPermission(profile, 'messages');
          const canAccessUsers = checkUserPermission(profile, 'volunteers') || checkUserPermission(profile, 'users') || checkUserPermission(profile, 'permissions');
          const canAccessEmailSettings = checkUserPermission(profile, 'emails') || checkUserPermission(profile, 'settings') || profile?.role === 'super_admin' || profile?.role === 'admin' || (user.email?.toLowerCase() === 'mrpostie1979@gmail.com');
          const canAccessEmailLogs = checkUserPermission(profile, 'emails') || checkUserPermission(profile, 'email_logs') || checkUserPermission(profile, 'audit') || profile?.role === 'super_admin' || profile?.role === 'admin' || (user.email?.toLowerCase() === 'mrpostie1979@gmail.com');

          if (canAccessReservations) {
            perfDiagnostics.recordCollectionRead('reservations', true);
            adminUnsubs.push(subscribeReservations(setReservations));
          }
          if (canAccessMessages) {
            perfDiagnostics.recordCollectionRead('customer_messages', true);
            adminUnsubs.push(subscribeCustomerMessages(setCustomerMessages));
          }
          perfDiagnostics.recordCollectionRead('volunteer_notifications', true);
          adminUnsubs.push(subscribeVolunteerNotifications(setVolunteerNotifications));

          if (canAccessUsers) {
            perfDiagnostics.recordCollectionRead('users', true);
            adminUnsubs.push(subscribeAllUsers(setAllUsers));
          } else {
            setAllUsers([]);
          }

          if (canAccessEmailSettings) {
            perfDiagnostics.recordCollectionRead('email_settings', true);
            adminUnsubs.push(subscribeEmailSettings((data) => { if (data) setEmailSettings(data); }));
          } else {
            setEmailSettings(DEFAULT_EMAIL_SETTINGS);
          }

          if (canAccessEmailLogs) {
            perfDiagnostics.recordCollectionRead('email_logs', true);
            adminUnsubs.push(subscribeEmailLogs(setEmailLogs));
          } else {
            setEmailLogs([]);
          }
        } catch (e) {
          console.error('Error initializing admin subscriptions/profile:', e);
        } finally {
          setIsLoadingProfile(false);
        }
      } else {
        setUserProfile(null);
        setUserProfileNotFound(null);
        setIsLoadingProfile(false);
        setReservations([]);
        setCustomerMessages([]);
        setVolunteerNotifications([]);
        setAllUsers([]);
        setEmailSettings(DEFAULT_EMAIL_SETTINGS);
        setEmailLogs([]);
      }
    });

    return () => {
      unsubAuth();
      cleanupAdminSubscriptions();
    };
  }, []);

  // Save Basket to LocalStorage
  useEffect(() => {
    localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify(basket));
  }, [basket]);

  // Handle PWA Install Prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const showToast = (message: string, type: 'success' | 'info' | 'error' = 'info') => {
    if (!message) return;
    const cleanMsg = getFriendlyAuthErrorMessage({ message });
    setToasts((prev) => {
      // Deduplicate: If an active toast already displays this exact message, do not add again
      if (prev.some((t) => t.message === cleanMsg)) {
        return prev;
      }
      const id = Math.random().toString(36).substring(2, 9);
      setTimeout(() => {
        removeToast(id);
      }, 4000);
      return [...prev, { id, message: cleanMsg, type }];
    });
  };

  const installPWA = () => {
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      deferredInstallPrompt.userChoice.then(() => {
        setDeferredInstallPrompt(null);
      });
    }
  };

  // Basket Actions
  const addToBasket = (product: Product, selectedSize: string, quantity = 1) => {
    const availableStock = getSizeStock(product, selectedSize);

    if (availableStock <= 0) {
      showToast(`Sorry, size ${selectedSize} for ${product.name} is currently out of stock.`, 'error');
      return;
    }

    setBasket((prev) => {
      const existingIdx = prev.findIndex(
        (item) => item.product.id === product.id && item.selectedSize === selectedSize
      );

      if (existingIdx > -1) {
        const existing = prev[existingIdx];
        const newQty = Math.min(availableStock, existing.quantity + quantity);
        if (existing.quantity + quantity > availableStock) {
          showToast(`Maximum available stock (${availableStock}) reached for size ${selectedSize}`, 'info');
        } else {
          showToast(`Updated quantity for ${product.name} (${selectedSize}) in basket`, 'info');
        }
        const updated = [...prev];
        updated[existingIdx] = { ...existing, quantity: newQty };
        return updated;
      } else {
        showToast(`Added ${product.name} (${selectedSize}) to reservation basket`, 'success');
        return [
          ...prev,
          {
            product,
            selectedSize,
            quantity: Math.min(availableStock, quantity)
          }
        ];
      }
    });
  };

  const removeFromBasket = (productId: string, selectedSize: string) => {
    setBasket((prev) => prev.filter((item) => !(item.product.id === productId && item.selectedSize === selectedSize)));
    showToast('Removed item from reservation cart', 'info');
  };

  const updateBasketQuantity = (productId: string, selectedSize: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromBasket(productId, selectedSize);
      return;
    }

    const currentItem = basket.find((item) => item.product.id === productId && item.selectedSize === selectedSize);
    if (!currentItem) return;

    const availableStock = getSizeStock(currentItem.product, selectedSize);

    if (quantity > availableStock) {
      showToast(`Cannot exceed available stock (${availableStock} left in size ${selectedSize})`, 'error');
      setBasket((prev) =>
        prev.map((item) =>
          item.product.id === productId && item.selectedSize === selectedSize
            ? { ...item, quantity: availableStock }
            : item
        )
      );
      return;
    }

    setBasket((prev) =>
      prev.map((item) =>
        item.product.id === productId && item.selectedSize === selectedSize
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearBasket = () => {
    setBasket([]);
    try {
      localStorage.removeItem(BASKET_STORAGE_KEY);
    } catch (e) {
      // ignore
    }
  };

  // Product Detail Modal
  const openProductDetail = (p: Product) => {
    setSelectedProductDetail(p);
  };

  const closeProductDetail = () => {
    setSelectedProductDetail(null);
  };

  // Reservation Actions
  const createReservation = async (details: {
    parentName: string;
    email: string;
    phone: string;
    preferredCollectionDay: string;
    optionalMessage?: string;
  }): Promise<Reservation> => {
    if (basket.length === 0) {
      throw new Error('Your reservation basket is empty');
    }

    const trimmedName = details.parentName?.trim();
    const trimmedEmail = details.email?.trim();
    const trimmedPhone = details.phone?.trim();
    const trimmedDay = details.preferredCollectionDay?.trim();

    if (!trimmedName || trimmedName.length < 2) {
      throw new Error('Please provide a valid Parent / Guardian name.');
    }
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      throw new Error('Please provide a valid email address.');
    }
    if (!trimmedPhone || trimmedPhone.length < 7) {
      throw new Error('Please provide a valid telephone number.');
    }
    if (!trimmedDay) {
      throw new Error('Please select a preferred collection slot.');
    }

    const reservationCode = generateShortReservationCode();
    const customerRef = getCustomerReference(reservationCode);
    const now = new Date();
    const expiry = new Date();
    expiry.setDate(now.getDate() + (settings.reservationExpiryDays || 7));

    const reservationItems = basket.map((b) => {
      const schoolObj = allSchools.find((s) => s.id === b.product.schoolId);
      return {
        productId: b.product.id,
        productName: b.product.name,
        schoolName: schoolObj?.name || 'Local Immingham School',
        size: b.selectedSize || b.product.size || 'Standard',
        quantity: b.quantity,
        image: b.product.images?.[0] || ''
      };
    });

    const newResData: Omit<Reservation, 'id'> = {
      reservationNumber: reservationCode,
      reservationCode: reservationCode,
      parentName: trimmedName,
      email: trimmedEmail.toLowerCase(),
      phone: trimmedPhone,
      items: reservationItems,
      status: 'Pending',
      preferredCollectionDay: trimmedDay,
      optionalMessage: details.optionalMessage?.trim() || '',
      createdAt: now.toISOString(),
      expiryDate: expiry.toISOString()
    };

    const newReservation = await createResFS(newResData, emailSettings, settings);

    clearBasket();
    try {
      sessionStorage.removeItem('ul_checkout_form');
      localStorage.removeItem('ul_checkout_form');
    } catch (e) {
      // ignore
    }
    setLatestReservation(newReservation);
    showToast(`Reservation ${customerRef} confirmed!`, 'success');
    return newReservation;
  };

  const updateReservationStatus = async (id: string, status: ReservationStatus, volunteerName?: string) => {
    try {
      await updateResStatusFS(id, status, emailSettings, settings, volunteerName || userProfile?.displayName || 'Volunteer');
    } catch (err: any) {
      console.error('Failed to update reservation status:', err);
      throw err;
    }
  };

  const deleteReservation = async (id: string) => {
    try {
      await deleteResFS(id);
      showToast('Reservation deleted and stock restored', 'info');
    } catch (err: any) {
      console.error(err);
      showToast(err.message || 'Failed to delete reservation', 'error');
    }
  };

  const updateReservationPhone = async (id: string, newPhone: string) => {
    await updatePhoneFS(id, newPhone);
  };

  const addReservationCustomerNote = async (id: string, noteMessage: string, parentName?: string) => {
    await addCustNoteFS(id, noteMessage, parentName);
  };

  const requestReservationDateChange = async (id: string, requestedDate: string, notes?: string) => {
    await requestDateChangeFS(id, requestedDate, notes);
  };

  const decideReservationDateChange = async (
    reservation: Reservation,
    decision: 'Approved' | 'Declined' | 'Rejected',
    decisionNotes?: string,
    volunteerName?: string
  ) => {
    await decideDateChangeFS(
      reservation,
      decision,
      decisionNotes,
      volunteerName || userProfile?.displayName || 'Volunteer',
      emailSettings
    );
  };

  const cancelReservationByCustomer = async (
    reservation: Reservation,
    cancellationReason?: string
  ) => {
    await cancelResByCustFS(
      reservation,
      cancellationReason,
      emailSettings,
      settings
    );
  };

  const markReservationCollected = async (id: string, volunteerName?: string) => {
    try {
      await markCollectedFS(id, volunteerName || userProfile?.displayName || 'Volunteer', emailSettings, settings);
      showToast('Reservation marked as Collected!', 'success');
    } catch (err: any) {
      console.error(err);
      showToast(err.message || 'Failed to mark as collected', 'error');
      throw err;
    }
  };

  const dismissVolunteerNotification = async (id: string) => {
    try {
      await dismissVolunteerNotifFS(id);
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
  };

  // Admin Stock Actions
  const addProduct = async (productData: Omit<Product, 'id' | 'createdAt'>): Promise<string> => {
    try {
      const newId = await addProdFS(productData);
      showToast('New uniform item added to live inventory!', 'success');
      return newId;
    } catch (err) {
      console.error(err);
      showToast('Failed to add product', 'error');
      throw err;
    }
  };

  const updateProduct = async (id: string, updates: Partial<Product>) => {
    try {
      await updateProdFS(id, updates);
      showToast('Uniform item details updated in live database', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to update product', 'error');
    }
  };

  const deleteProduct = async (id: string) => {
    try {
      await deleteProdFS(id);
      showToast('Item removed from inventory', 'info');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete product', 'error');
    }
  };

  const duplicateProduct = async (id: string) => {
    const original = products.find((p) => p.id === id);
    if (!original) return;
    try {
      const { id: _, createdAt: __, ...prodWithoutId } = original;
      await addProdFS({
        ...prodWithoutId,
        name: `${original.name} (Copy)`
      });
      showToast('Product duplicated', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to duplicate product', 'error');
    }
  };

  const adjustProductQuantity = async (id: string, delta: number) => {
    const prod = products.find((p) => p.id === id);
    if (!prod) return;
    const targetSize = prod.sizes?.[0]?.size || prod.size || 'One Size';
    try {
      await adjustSizeStockFS(id, targetSize, delta);
      showToast('Stock count adjusted', 'info');
    } catch (err) {
      console.error(err);
      showToast('Failed to adjust stock', 'error');
    }
  };

  const adjustProductSizeStock = async (productId: string, sizeLabel: string, delta: number) => {
    try {
      await adjustSizeStockFS(productId, sizeLabel, delta);
      showToast(`Stock count adjusted for size ${sizeLabel}`, 'info');
    } catch (err) {
      console.error(err);
      showToast('Failed to adjust size stock', 'error');
    }
  };

  // School Actions
  const addSchool = async (school: Omit<School, 'id'>) => {
    try {
      const newId = await addSchoolFS(school);
      showToast('School added successfully!', 'success');
      return newId;
    } catch (err) {
      console.error(err);
      showToast('Failed to add school', 'error');
      throw err;
    }
  };

  const updateSchool = async (id: string, updates: Partial<School>) => {
    try {
      await updateSchoolFS(id, updates);
      showToast('School updated successfully!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to update school', 'error');
      throw err;
    }
  };

  const deleteSchool = async (id: string) => {
    try {
      await deleteSchoolFS(id);
      showToast('School deleted', 'info');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete school', 'error');
      throw err;
    }
  };

  const reorderSchools = async (orderedSchools: School[]) => {
    try {
      await reorderSchoolsFS(orderedSchools);
      showToast('School order updated!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to reorder schools', 'error');
      throw err;
    }
  };

  // Category Actions
  const addCategory = async (cat: Category) => {
    try {
      await addCategoryFS(cat);
      showToast('Category created successfully!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to create category', 'error');
      throw err;
    }
  };

  const updateCategory = async (id: string, updates: Partial<Category>) => {
    try {
      await updateCategoryFS(id, updates);
      showToast('Category updated successfully!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to update category', 'error');
      throw err;
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      await deleteCategoryFS(id);
      showToast('Category deleted successfully', 'info');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete category', 'error');
      throw err;
    }
  };

  const swapCategoryOrder = async (
    catA: { id: string; displayOrder: number },
    catB: { id: string; displayOrder: number }
  ) => {
    try {
      await swapCategoryOrderFS(catA, catB);
    } catch (err) {
      console.error(err);
      showToast('Failed to reorder categories', 'error');
      throw err;
    }
  };

  // News Actions
  const addNewsItem = async (item: Omit<NewsItem, 'id' | 'createdAt'>) => {
    try {
      await addNewsFS(item);
      showToast('News announcement posted to website!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to post news announcement', 'error');
    }
  };

  const updateNewsItem = async (id: string, updates: Partial<NewsItem>) => {
    try {
      await updateNewsFS(id, updates);
      showToast('News announcement updated', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to update news item', 'error');
    }
  };

  const deleteNewsItem = async (id: string) => {
    try {
      await deleteNewsFS(id);
      showToast('News announcement deleted', 'info');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete news item', 'error');
    }
  };

  // Settings & Stats
  const updateSettings = async (updates: Partial<SiteSettings>) => {
    try {
      await updateSettingsFS(updates);
      showToast('Homepage and Community Hub settings saved!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to save settings', 'error');
    }
  };

  const updateStats = async (updates: Partial<ImpactStats>) => {
    try {
      await updateStatsFS(updates);
      showToast('Impact metrics updated', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to save impact stats', 'error');
    }
  };

  // Customer Messages
  const createCustomerMessage = async (
    msgData: Omit<CustomerMessage, 'id' | 'createdAt' | 'status'>
  ): Promise<CustomerMessage> => {
    try {
      const newMsg = await createMsgFS(msgData, emailSettings);
      showToast('Enquiry sent to the volunteer team! We will reply soon.', 'success');
      return newMsg;
    } catch (err: any) {
      console.error(err);
      showToast(err.message || 'Failed to send message', 'error');
      throw err;
    }
  };

  const updateCustomerMessageStatus = async (id: string, status: MessageStatus) => {
    try {
      await updateMsgStatusFS(id, status);
      showToast(`Message status updated to ${status}`, 'success');
    } catch (err: any) {
      console.error(err);
      showToast('Failed to update message status', 'error');
    }
  };

  const addCustomerMessageReply = async (
    id: string,
    replyBody: string,
    isInternalNote: boolean = false,
    customStatus?: MessageStatus
  ) => {
    try {
      const msg = customerMessages.find((m) => m.id === id);
      if (!msg) throw new Error('Message not found');
      const senderName = userProfile?.displayName || 'Volunteer Team';
      await addMsgReplyFS(id, msg, senderName, replyBody, isInternalNote, customStatus, emailSettings);
      if (isInternalNote) {
        showToast('Internal note saved to ticket!', 'info');
      } else {
        showToast(`Email sent to ${msg.email}!`, 'success');
      }
    } catch (err: any) {
      console.error(err);
      showToast('Failed to send reply', 'error');
    }
  };

  const assignCustomerMessage = async (id: string, volunteerName: string) => {
    try {
      await assignMsgFS(id, volunteerName);
      showToast(`Assigned to ${volunteerName}`, 'info');
    } catch (err: any) {
      console.error(err);
      showToast('Failed to assign message', 'error');
    }
  };

  const deleteCustomerMessage = async (id: string) => {
    try {
      await deleteMsgFS(id);
      showToast('Message deleted', 'info');
    } catch (err: any) {
      console.error(err);
      showToast('Failed to delete message', 'error');
    }
  };

  // Admin Auth & User Creation
  const loginAdmin = async (email: string, password?: string): Promise<boolean> => {
    try {
      const pass = password || 'admin123';
      await loginAdminWithEmail(email, pass);
      setIsAdminLoggedIn(true);
      showToast('Authenticated successfully', 'success');
      return true;
    } catch (err: any) {
      console.error('Login error:', err);
      showToast(err.message || 'Incorrect login credentials', 'error');
      return false;
    }
  };

  const registerAdmin = async (email: string, password: string, displayName?: string): Promise<boolean> => {
    try {
      await registerAdminWithEmailAuth(email, password, displayName);
      setIsAdminLoggedIn(true);
      showToast('Admin account created and signed in successfully!', 'success');
      return true;
    } catch (err: any) {
      console.error('Registration error:', err);
      showToast(err.message || 'Could not create account', 'error');
      return false;
    }
  };

  const sendPasswordReset = async (email: string): Promise<boolean> => {
    try {
      await sendPasswordResetEmailAuth(email);
      showToast('Password reset email sent! Check your inbox.', 'success');
      return true;
    } catch (err: any) {
      console.error('Password reset error:', err);
      showToast(err.message || 'Could not send reset email', 'error');
      return false;
    }
  };

  const logoutAdmin = async () => {
    try {
      await logoutAdminAuth();
      setIsAdminLoggedIn(false);
      setUserProfile(null);
      setUserProfileNotFound(null);
      setIsLoadingProfile(false);
      showToast('Logged out of Admin Portal', 'info');
    } catch (err) {
      console.error(err);
    }
  };

  const repairMissingProfile = async (role?: UserRole, permissions?: AppPermission[]) => {
    if (!userProfileNotFound) {
      showToast('No missing profile detected.', 'info');
      return;
    }
    try {
      const repaired = await repairUserFS({
        uid: userProfileNotFound.uid,
        email: userProfileNotFound.email,
        displayName: userProfileNotFound.displayName,
        role: role || (userProfileNotFound.email.toLowerCase() === 'mrpostie1979@gmail.com' ? 'super_admin' : 'volunteer'),
        permissions
      });
      setUserProfile(repaired);
      setUserProfileNotFound(null);
      showToast(`Firestore user profile created for ${repaired.displayName}!`, 'success');
    } catch (err: any) {
      console.error('Failed to repair profile:', err);
      showToast(err.message || 'Failed to repair profile', 'error');
    }
  };

  const createVolunteerUser = async (data: {
    email: string;
    password: string;
    displayName: string;
    phone?: string;
    role?: UserRole;
    rolePresetId?: string;
    roleTitle?: string;
    permissions?: AppPermission[];
    createdBy?: string;
  }) => {
    try {
      await createVolunteerFS({
        ...data,
        createdBy: data.createdBy || userProfile?.displayName || 'Administrator'
      });
      showToast(`User account created for ${data.displayName}!`, 'success');
    } catch (err: any) {
      console.error('Error creating user account:', err);
      const friendlyMsg = getFriendlyAuthErrorMessage(err);
      throw new Error(friendlyMsg);
    }
  };

  const updateUserRoleOrStatus = async (uid: string, updates: Partial<UserProfile>) => {
    try {
      await updateRoleFS(uid, updates);
      showToast('User profile and permissions saved!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to update user account', 'error');
    }
  };

  const deleteVolunteerUser = async (uid: string) => {
    try {
      await deleteUserFS(uid);
      showToast('User account deleted from system', 'info');
    } catch (err) {
      console.error(err);
      showToast('Failed to delete user', 'error');
    }
  };

  const hasUserPermission = (permission: AppPermission): boolean => {
    return checkUserPermission(userProfile, permission);
  };

  // Email System Settings
  const updateEmailSettings = async (updates: Partial<EmailSettings>) => {
    try {
      await updateEmailSettingsFS(updates);
      showToast('Email system configuration updated successfully!', 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to update email settings', 'error');
      throw err;
    }
  };

  const sendTestEmail = async (recipientEmail: string) => {
    try {
      const res = await sendTestEmailFS(recipientEmail, emailSettings);
      if (res.success) {
        showToast(`Test email successfully sent to ${recipientEmail}!`, 'success');
      } else {
        showToast(`Test email warning: ${res.error || 'Check log'}`, 'error');
      }
      return res;
    } catch (err: any) {
      console.error(err);
      showToast(`Test email failed: ${err.message}`, 'error');
      return { success: false, deliveryStatus: 'failed', error: err.message };
    }
  };

  const pruneEmailLogs = async (retentionDays = 30): Promise<{ deletedCount: number; cutoffDate: string }> => {
    try {
      const result = await cleanupOldEmailLogs(retentionDays);
      if (result.deletedCount > 0) {
        showToast(`Cleaned up ${result.deletedCount} expired email logs older than ${retentionDays} days.`, 'success');
      } else {
        showToast(`Audit log is already optimized. No logs older than ${retentionDays} days found.`, 'info');
      }
      return result;
    } catch (err: any) {
      console.error('Failed to prune email logs:', err);
      showToast(`Failed to cleanup email logs: ${err.message}`, 'error');
      throw err;
    }
  };

  const isSuperAdmin = userProfile?.role === 'super_admin';
  const isVolunteer = userProfile?.role === 'volunteer';
  const isAdministrator = useMemo(() => checkIsAdministrator(userProfile), [userProfile]);

  // Ensure opening hours string is formatted from openingHoursSchedule if present
  const effectiveSettings: SiteSettings = useMemo(() => {
    const schedule = settings.openingHoursSchedule || DEFAULT_OPENING_SCHEDULE;
    const formattedHours = formatOpeningHours(schedule, settings.openingHours);
    return {
      ...settings,
      openingHours: formattedHours,
      openingHoursSchedule: schedule
    };
  }, [settings]);

  // Throttled 30-Day Archive Retention Cleanup (Runs at most once every 24 hours for logged-in admin)
  useEffect(() => {
    if (!isAdminLoggedIn || reservations.length === 0) return;
    const LAST_PURGE_KEY = 'ul_last_archive_purge_v1';
    try {
      const lastRun = localStorage.getItem(LAST_PURGE_KEY);
      const now = Date.now();
      if (lastRun && now - Number(lastRun) < 24 * 60 * 60 * 1000) {
        return;
      }
      localStorage.setItem(LAST_PURGE_KEY, String(now));
      purgeExpiredArchivedReservations(reservations).catch((err) => {
        console.warn('Background archive purge note:', err);
      });
    } catch {
      // Non-blocking
    }
  }, [isAdminLoggedIn]);

  // Lifetime Permanent Impact Statistics (Stored in stats/impact, never reduced on archive deletion)
  const effectiveStats: ImpactStats = useMemo(() => {
    const activeVolunteersCount = allUsers.filter((u) => u.status !== 'inactive').length;
    const activeSchoolsCount = schools.filter((s) => s.active !== false).length;

    // Calculate baseline from current active/collected records for fallback if Firestore stats doc is fresh/uninitialized
    const completedRes = reservations.filter((r) => r.status === 'Collected');
    const baselineFamilies = new Set(
      completedRes
        .map((r) => (r.email || r.phone || r.parentName || '').toLowerCase().trim())
        .filter(Boolean)
    ).size;
    const baselineItemsReused = completedRes.reduce((acc, r) => {
      const count = r.items ? r.items.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0;
      return acc + count;
    }, 0);

    const currentStockQty = products.reduce((acc, p) => acc + getTotalStock(p), 0);

    // Lifetime values are strictly non-decreasing (max of permanent Firestore doc vs active baseline)
    const familiesHelped = Math.max(stats?.familiesHelped || 0, baselineFamilies);
    const itemsReused = Math.max(stats?.itemsReused || 0, baselineItemsReused);
    const itemsDonated = Math.max(stats?.itemsDonated || 0, currentStockQty + itemsReused);

    return {
      familiesHelped,
      itemsReused,
      itemsDonated,
      volunteers: activeVolunteersCount,
      schoolsSupported: activeSchoolsCount
    };
  }, [stats, reservations, products, schools, allUsers]);

  return (
    <ShopContext.Provider
      value={{
        products,
        categories,
        schools,
        allSchools,
        reservations,
        news,
        settings: effectiveSettings,
        stats: effectiveStats,
        basket,
        customerMessages,
        isLoadingData,
        activePage,
        setActivePage,
        selectedSchoolFilter,
        setSelectedSchoolFilter,
        selectedCategoryFilter,
        setSelectedCategoryFilter,
        searchQuery,
        setSearchQuery,
        isBasketOpen,
        setIsBasketOpen,
        selectedProductDetail,
        openProductDetail,
        closeProductDetail,
        latestReservation,
        setLatestReservation,
        addToBasket,
        removeFromBasket,
        updateBasketQuantity,
        clearBasket,
        createReservation,
        updateReservationStatus,
        deleteReservation,
        updateReservationPhone,
        addReservationCustomerNote,
        requestReservationDateChange,
        decideReservationDateChange,
        cancelReservationByCustomer,
        markReservationCollected,
        volunteerNotifications,
        dismissVolunteerNotification,
        addProduct,
        updateProduct,
        deleteProduct,
        duplicateProduct,
        adjustProductQuantity,
        adjustProductSizeStock,
        addSchool,
        updateSchool,
        deleteSchool,
        reorderSchools,
        addCategory,
        updateCategory,
        deleteCategory,
        swapCategoryOrder,
        addNewsItem,
        updateNewsItem,
        deleteNewsItem,
        updateSettings,
        updateStats,
        createCustomerMessage,
        updateCustomerMessageStatus,
        addCustomerMessageReply,
        assignCustomerMessage,
        deleteCustomerMessage,
        isAdminLoggedIn,
        isLoadingProfile,
        userProfileNotFound,
        userProfile,
        userRole: userProfile?.role || null,
        isSuperAdmin,
        isAdministrator,
        isVolunteer,
        allUsers,
        activeAdminTab,
        setActiveAdminTab,
        loginAdmin,
        registerAdmin,
        sendPasswordReset,
        logoutAdmin,
        repairMissingProfile,
        createVolunteerUser,
        updateUserRoleOrStatus,
        deleteVolunteerUser,
        hasUserPermission,
        emailSettings,
        emailLogs,
        updateEmailSettings,
        sendTestEmail,
        pruneEmailLogs,
        toasts,
        showToast,
        removeToast,
        deferredInstallPrompt,
        installPWA
      }}
    >
      {children}
    </ShopContext.Provider>
  );
};

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};
